import * as Elevated from '@pitaman71/omniglot-introspect';
import * as Clients from '../Clients';
import * as Stores from '../Stores';
import { RelationStream } from '../Streams';

const __moduleName__ = `omniglot-live-data.Relations`;

import * as Objects from '../Objects';

export interface Builder<BuilderBindingType extends Objects.BindingType> {
    object(symbolName: keyof BuilderBindingType): void;

    scalar(): void;
    set(): void;
    sequence(): void;
}

export abstract class Descriptor<DescriptorBindingType extends Objects.BindingType = Objects.BindingType> {
    abstract canonicalName: string;
    abstract build(builder: Builder<DescriptorBindingType>): void;

    static Directory = new Elevated.Directory<Descriptor<any>>(`${__moduleName__}.Descriptor`);

    spawn(from: Partial<DescriptorBindingType>): DescriptorBindingType {
        const result: Partial<DescriptorBindingType> = { ...from };
        const symbols: (keyof DescriptorBindingType)[] = [];
        this.build(new class {
            object(symbolName: keyof DescriptorBindingType): void {
                symbols.push(symbolName)
            }
            scalar(): void {}
            set(): void {}
            sequence(): void {}
        });

        let spawnCount = 0;
        for(let symbol of symbols) {
            const binding = result[symbol];
            if(binding === undefined || binding.status === 'new') {
                ++spawnCount;
                result[symbol] = Objects.Binding.from_bound(Elevated.factories.makeObjectId()) as any;
            }
        }
        return (spawnCount > 0 ? result : from ) as DescriptorBindingType;
    }    
    bindAnchor(
        binding: Partial<DescriptorBindingType>
    ): Anchor<DescriptorBindingType> {
        return { descriptor: this, binding }
    }

    bindInstance(
        binding: DescriptorBindingType
    ): Instance<DescriptorBindingType> {
        return { descriptor: this, binding }
    }

    stream(
        zone: Stores.Zone,
        binding: Partial<DescriptorBindingType>
    ): RelationStream<DescriptorBindingType> {
        return zone.streams().relation(this.bindAnchor(binding));
    }
    eager(zone: Stores.Zone, binding: Partial<DescriptorBindingType>) {
        const stream = this.stream(zone, binding);
        if(!stream) return undefined;
        return Clients.RelationClient.Eager<DescriptorBindingType>(this.canonicalName).upstream(stream);
    }

    getSymbols(options: { from: boolean, to: boolean }): (keyof DescriptorBindingType)[] {
        let mode: 'left'|'right' = 'left';
        const symbols: (keyof DescriptorBindingType)[] = [];
        this.build(new class {
            object(symbolName: keyof DescriptorBindingType): void {
                if(options.from && mode === 'left' || options.to && mode === 'right')
                    symbols.push(symbolName)
            }
            scalar(): void { mode = 'right'; }
            set(): void { mode = 'right';}
            sequence(): void { mode= 'right' }
        });
        return symbols;
    }
}

export interface Anchor<AnhorBindingType extends Objects.BindingType> {
    descriptor: Descriptor<AnhorBindingType>;
    binding: Partial<AnhorBindingType>;
}

export interface Instance<InstanceBindingType extends Objects.BindingType> {
    descriptor: Descriptor<InstanceBindingType>;
    binding: InstanceBindingType;
}
